import { useState, useEffect, useCallback } from 'react';

const API =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  'http://127.0.0.1:5000';

const tok = () => localStorage.getItem('portalToken');

const when = (iso) => (iso ? new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// "Assigned to" bar on a candidate profile: hand the profile to a Talent Pool
// staff member with an internal note (they're emailed it), mark it done with
// an optional reply (the assigner is emailed), reassign, and see history.
export default function AssignmentBar({ applicantId }) {
  const [data, setData] = useState({ current: null, history: [], staff: [] });
  const [mode, setMode] = useState(''); // '' | 'assign' | 'done'
  const [assignee, setAssignee] = useState('');
  const [note, setNote] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/applicants/${applicantId}/assignments`, { headers: { Authorization: `Bearer ${tok()}` } });
      if (res.ok) setData(await res.json());
    } catch { /* bar just stays empty */ }
  }, [applicantId]);

  useEffect(() => { load(); }, [load]);

  const post = async (path, body, okMsg) => {
    setBusy(true); setMsg('');
    try {
      const res = await fetch(`${API}/api/admin/applicants/${applicantId}/assignments${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not save');
      setMsg(d.email_sent === false ? `${okMsg} (but the email could not be sent)` : okMsg);
      setMode(''); setNote(''); setReply(''); setAssignee('');
      await load();
    } catch (e) {
      setMsg(e.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const cur = data.current;
  const box = { marginTop: 16, padding: '12px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border2)',
                background: cur ? '#fff8e1' : 'var(--bg3)', fontSize: 13 };
  const textarea = { width: '100%', boxSizing: 'border-box', marginTop: 6, fontFamily: 'inherit', fontSize: 13 };

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>📌 {cur ? <>Assigned to {cur.assigned_to_name}</> : 'Not assigned'}</span>
        {cur && <span style={{ color: 'var(--text3)' }}>by {cur.assigned_by_name} · {when(cur.created_at)}</span>}
        <span style={{ flex: 1 }} />
        {cur && mode === '' && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setMode('done')}>Mark done</button>
        )}
        {mode === '' && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setMode('assign')}>{cur ? 'Reassign' : 'Assign'}</button>
        )}
      </div>

      {cur && (
        <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff', borderLeft: '3px solid #9c27b0', whiteSpace: 'pre-wrap' }}>{cur.note}</div>
      )}

      {mode === 'assign' && (
        <div style={{ marginTop: 10 }}>
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)} style={{ width: '100%' }}>
            <option value="">— Assign to —</option>
            {data.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <textarea rows={3} style={textarea} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Internal note — what needs updating? (emailed to them)" />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setMode(''); setMsg(''); }}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || !assignee || !note.trim()}
              onClick={() => post('', { assigned_to_id: Number(assignee), note }, 'Assigned — they have been emailed your note.')}>
              {busy ? 'Saving…' : 'Assign & email'}
            </button>
          </div>
        </div>
      )}

      {mode === 'done' && cur && (
        <div style={{ marginTop: 10 }}>
          <textarea rows={2} style={textarea} value={reply} onChange={(e) => setReply(e.target.value)}
            placeholder={`Reply note for ${cur.assigned_by_name} (optional)`} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setMode(''); setMsg(''); }}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy}
              onClick={() => post(`/${cur.id}/done`, { reply_note: reply }, 'Marked done.')}>
              {busy ? 'Saving…' : 'Mark done'}
            </button>
          </div>
        </div>
      )}

      {msg && <p style={{ margin: '8px 0 0', fontSize: 12, color: /could not|choose|please|no longer/i.test(msg) ? 'var(--red)' : 'var(--green)' }}>{msg}</p>}

      {data.history.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => setShowHistory((v) => !v)}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text3)', fontSize: 12, cursor: 'pointer' }}>
            {showHistory ? '▾' : '▸'} Earlier assignments ({data.history.length})
          </button>
          {showHistory && data.history.map((h) => (
            <div key={h.id} style={{ marginTop: 6, paddingLeft: 10, borderLeft: '2px solid var(--border2)', fontSize: 12 }}>
              <b>{h.assigned_to_name}</b> · by {h.assigned_by_name} · {when(h.created_at)} ·{' '}
              {h.status === 'done' ? `done ${when(h.closed_at)}` : 'reassigned'}
              <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text2)' }}>{h.note}</div>
              {h.reply_note && <div style={{ whiteSpace: 'pre-wrap', color: 'var(--green)' }}>↳ {h.reply_note}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
